import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

import queryGraphql from "../../../helpers/ApiInterface";
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer";
import DataSourcesRequestsTable from "./DataSourcesRequestsTable";
import MiniBot from "../../MiniBot";

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

let searchOptions: SearchOptions[] = [];

const DataSourcesRequestsPage: React.FC = () => {
    const [dataSources, setDataSources] = useState<any[]>([]); // State variable for Data Source data
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [updates, setUpdates] =  useState<any[]>([]);
    const navigate = useNavigate();

    React.useEffect(() =>{
        const fetchData = async () => {
            try {
                const data = await queryGraphql(`
                        query Query {
                        resourceStatuses {
                            id
                            currentStatus
                            resourceId
                        }
                        }
                    `);
                if(data.resourceStatuses){
                    setUpdates(data.resourceStatuses);
                }
                }
                catch(e){
            console.log(e);
                }
    }
    fetchData();
    },[]);  
    
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // Using the getDataSources query
                const data = await queryGraphql(`
                    query GetDataSources {
                      getDataSources {
                        id
                        name
                        solutionType
                        cloudProvider
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          dataSources {
                            projectId
                            location
                            lake
                            zone
                            resourceSpec {
                              type
                            }
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const rawDataSourcesData = data.data?.getDataSources || data.getDataSources || [];

                let dataSourcesArr = rawDataSourcesData.map((e: any) => {
                    return {
                        id: e.id,
                        dataSourceName: e.name,
                        status: e.status ? e.status.toLowerCase() : 'pending',
                        createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
                        actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
                        dataSourceOwnerName: e.solutionOwner?.name,
                        dataSourceOwnerAvatar: e.solutionOwner?.googleProfilePicture,
                        
                        dataSourceApproverName: e.solutionApprover?.name,
                        dataSourceApproverAvatar: e.solutionApprover?.googleProfilePicture,

                        // Changed from solutionCategoryType to resourceSpecType
                        resourceSpecType: e.details?.dataSources?.resourceSpec?.type, 
                        cloudProvider: e.cloudProvider,
                        projectId: e.details?.dataSources?.projectId,

                        reasonForRejection: e.reasonForRejection,
                    };
                });
                setDataSources(dataSourcesArr);

                searchOptions = dataSourcesArr
                    .map((e: any) => {
                        const options = [
                            {
                                category: "dataSourceName",
                                groupBy: "DATA SOURCE NAME",
                                chipLabel: `Source Name: ${e.dataSourceName}`,
                                dropdownLabel: e.dataSourceName,
                            },
                            {
                                category: "dataSourceOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${e.dataSourceOwnerName}`,
                                dropdownLabel: e.dataSourceOwnerName,
                            },
                            {
                                category: "dataSourceApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${e.dataSourceApproverName}`,
                                dropdownLabel: e.dataSourceApproverName,
                            },
                        ];
                        
                        if (e.resourceSpecType) {
                            options.push({
                               category: "resourceSpecType",
                               groupBy: "DATA SOURCE TYPE",
                               chipLabel: `Type: ${e.resourceSpecType}`,
                               dropdownLabel: e.resourceSpecType,
                           });
                       }
                       if (e.cloudProvider) {
                        options.push({
                           category: "cloudProvider",
                           groupBy: "CLOUD PROVIDER",
                           chipLabel: `Provider: ${e.cloudProvider}`,
                           dropdownLabel: e.cloudProvider,
                       });
                   }
                        if (e.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${e.status.charAt(0).toUpperCase() + e.status.slice(1)}`,
                                dropdownLabel: e.status.charAt(0).toUpperCase() + e.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    const filteredDataSources =
        searchQuery.length === 0
            ? dataSources
            : dataSources.filter(
                    (dataSource: any) =>
                        (searchQuery.filter((e) => e.category === "dataSourceName").length > 0
                            ? searchQuery.filter((e) => e.category === "dataSourceName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "dataSourceOwnerName").length > 0
                            ? searchQuery.filter((e) => e.category === "dataSourceOwnerName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceOwnerName?.toLowerCase())
                            : true) &&
                         (searchQuery.filter((e) => e.category === "dataSourceApproverName").length > 0
                            ? searchQuery.filter((e) => e.category === "dataSourceApproverName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceApproverName?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "resourceSpecType").length > 0
                            ? searchQuery.filter((e) => e.category === "resourceSpecType").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.resourceSpecType?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
                            ? searchQuery.filter((e) => e.category === "cloudProvider").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.cloudProvider?.toLowerCase())
                            : true) &&
                        (searchQuery.filter((e) => e.category === "status").length > 0
                            ? searchQuery.filter((e) => e.category === "status").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.status?.toLowerCase())
                            : true)
              );
    
    const filteredActiveDataSources = filteredDataSources.filter(
        (p: any) => p.status === "active" || p.status === "approved"
    );
    const filteredPendingDataSources = filteredDataSources.filter(
        (p: any) => p.status === "pending"
    );
    const filteredRejectedDataSources = filteredDataSources.filter(
        (p: any) => p.status === "rejected"
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-data-sources"} />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: window.innerHeight,
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                            <IconButton
                                aria-label="Help" 
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{ padding: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' } }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40,  height: 40, display: 'block' }} />
                              </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
                            <Box>
                                <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">
                                    {`DATA SOURCES (${dataSources.length})`}
                                </Typography>
                                <Typography fontFamily="Gilroy-Regular" fontSize="14px">
                                    {`View all the requests raised for Data Sources`}
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ));
                                }}
                                onChange={(e, newVal, reason) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by source name, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img src="/assets/search-normal.svg" alt="Filter Icon" style={{ width: "16px", paddingLeft: "4px" }}/>
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingDataSources.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingDataSources.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Data Sources ({filteredPendingDataSources.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingDataSources.length > 0 && (
                                    <DataSourcesRequestsTable dataSources={filteredPendingDataSources} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveDataSources.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Data Sources ({filteredActiveDataSources.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveDataSources.length > 0 && (
                                    <DataSourcesRequestsTable dataSources={filteredActiveDataSources} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedDataSources.length > 0 ? true : false}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedDataSources.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Data Sources ({filteredRejectedDataSources.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedDataSources.length > 0 && (
                                    <DataSourcesRequestsTable dataSources={filteredRejectedDataSources} resourceStatuses={updates}/>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataSourcesRequestsPage;



// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";

// import queryGraphql from "../../../helpers/ApiInterface";
// import { ExpandMore } from "@mui/icons-material";
// import MainDrawer from "../../MainDrawer";
// import DataSourcesRequestsTable from "./DataSourcesRequestsTable"; // Renamed import
// import MiniBot from "../../MiniBot";

// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: string;
//     groupBy: string;
// }

// let searchOptions: SearchOptions[] = [];

// const DataSourcesRequestsPage: React.FC = () => {
//     const [dataSources, setDataSources] = useState<any[]>([]); // State variable for Data Source data
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const [updates, setUpdates] =  useState<any[]>([]);
//     const navigate = useNavigate();

//     React.useEffect(() =>{
//         const fetchData = async () => {
//             try {
//                 const data = await queryGraphql(`
//                         query Query {
//                         resourceStatuses {
//                             id
//                             currentStatus
//                             resourceId
//                         }
//                         }
//                     `);
//                 if(data.resourceStatuses){
//                     setUpdates(data.resourceStatuses);
//                 }
//                 }
//                 catch(e){
//             console.log(e);
//                 }
//     }
//     fetchData();
//     },[]);  
    
//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 // Using the getDataSources query
//                 const data = await queryGraphql(`
//                     query GetDataSources {
//                       getDataSources {
//                         id
//                         name
//                         solutionType
//                         solutionCategoryType
//                         environment
//                         cloudProvider
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         status
//                         details {
//                           dataSources {
//                             projectId
//                             location
//                             lake
//                             zone
//                             zoneType
//                           }
//                         }
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                           email
//                           googleProfilePicture
//                         }
//                         solutionApprover {
//                           id
//                           name
//                           email
//                           googleProfilePicture
//                         }
//                         application {
//                           id
//                           applicationName
//                         }
//                       }
//                     }
//                 `);

//                 const rawDataSourcesData = data.data?.getDataSources || data.getDataSources || [];

//                 let dataSourcesArr = rawDataSourcesData.map((e: any) => {
//                     return {
//                         id: e.id,
//                         dataSourceName: e.name,
//                         applicationName: e.application?.applicationName,
//                         status: e.status ? e.status.toLowerCase() : 'pending',
//                         createdAt: e.createdAt ? new Date(Number(e.createdAt)).toISOString() : null,
//                         actionedAt: e.updatedAt ? new Date(Number(e.updatedAt)).toISOString() : null,
                        
//                         dataSourceOwnerName: e.solutionOwner?.name,
//                         dataSourceOwnerAvatar: e.solutionOwner?.googleProfilePicture,
                        
//                         dataSourceApproverName: e.solutionApprover?.name,
//                         dataSourceApproverAvatar: e.solutionApprover?.googleProfilePicture,

//                         solutionCategoryType: e.solutionCategoryType,
//                         cloudProvider: e.cloudProvider,
//                         projectId: e.details?.dataSources?.projectId,

//                         reasonForRejection: e.reasonForRejection,
//                     };
//                 });
//                 setDataSources(dataSourcesArr);

//                 searchOptions = dataSourcesArr
//                     .map((e: any) => {
//                         const options = [
//                             {
//                                 category: "dataSourceName",
//                                 groupBy: "DATA SOURCE NAME",
//                                 chipLabel: `Source Name: ${e.dataSourceName}`,
//                                 dropdownLabel: e.dataSourceName,
//                             },
//                             {
//                                 category: "dataSourceOwnerName",
//                                 groupBy: "OWNER",
//                                 chipLabel: `Owner: ${e.dataSourceOwnerName}`,
//                                 dropdownLabel: e.dataSourceOwnerName,
//                             },
//                             {
//                                 category: "dataSourceApproverName",
//                                 groupBy: "APPROVER",
//                                 chipLabel: `Approver: ${e.dataSourceApproverName}`,
//                                 dropdownLabel: e.dataSourceApproverName,
//                             },
//                         ];
//                         if (e.applicationName) {
//                              options.push({
//                                 category: "applicationName",
//                                 groupBy: "APPLICATION NAME",
//                                 chipLabel: `Application: ${e.applicationName}`,
//                                 dropdownLabel: e.applicationName,
//                             });
//                         }
//                         if (e.solutionCategoryType) {
//                             options.push({
//                                category: "solutionCategoryType",
//                                groupBy: "CATEGORY TYPE",
//                                chipLabel: `Category: ${e.solutionCategoryType}`,
//                                dropdownLabel: e.solutionCategoryType,
//                            });
//                        }
//                        if (e.cloudProvider) {
//                         options.push({
//                            category: "cloudProvider",
//                            groupBy: "CLOUD PROVIDER",
//                            chipLabel: `Provider: ${e.cloudProvider}`,
//                            dropdownLabel: e.cloudProvider,
//                        });
//                    }
//                         if (e.status) {
//                             options.push({
//                                 category: "status",
//                                 groupBy: "STATUS",
//                                 chipLabel: `Status: ${e.status.charAt(0).toUpperCase() + e.status.slice(1)}`,
//                                 dropdownLabel: e.status.charAt(0).toUpperCase() + e.status.slice(1),
//                             });
//                         }
//                         return options;
//                     })
//                     .flat();

//                 searchOptions = searchOptions.filter(
//                     (obj, index, self) =>
//                         obj.dropdownLabel &&
//                         index ===
//                         self.findIndex(
//                             (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                         )
//                 );
//                 searchOptions.sort((a, b) => {
//                     if (a.groupBy > b.groupBy) return 1;
//                     if (a.groupBy < b.groupBy) return -1;
//                     if (a.dropdownLabel > b.dropdownLabel) return 1;
//                     if (a.dropdownLabel < b.dropdownLabel) return -1;
//                     return 0;
//                 });

//             } catch (error) {
//                 console.log(error);
//             }
//         };

//         fetchData();
//     }, []);

//     const filteredDataSources =
//         searchQuery.length === 0
//             ? dataSources
//             : dataSources.filter(
//                     (dataSource: any) =>
//                         (searchQuery.filter((e) => e.category === "dataSourceName").length > 0
//                             ? searchQuery.filter((e) => e.category === "dataSourceName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceName?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "dataSourceOwnerName").length > 0
//                             ? searchQuery.filter((e) => e.category === "dataSourceOwnerName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceOwnerName?.toLowerCase())
//                             : true) &&
//                          (searchQuery.filter((e) => e.category === "dataSourceApproverName").length > 0
//                             ? searchQuery.filter((e) => e.category === "dataSourceApproverName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.dataSourceApproverName?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "applicationName").length > 0
//                             ? searchQuery.filter((e) => e.category === "applicationName").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.applicationName?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "solutionCategoryType").length > 0
//                             ? searchQuery.filter((e) => e.category === "solutionCategoryType").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.solutionCategoryType?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
//                             ? searchQuery.filter((e) => e.category === "cloudProvider").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.cloudProvider?.toLowerCase())
//                             : true) &&
//                         (searchQuery.filter((e) => e.category === "status").length > 0
//                             ? searchQuery.filter((e) => e.category === "status").map((e) => e.dropdownLabel?.toLowerCase()).includes(dataSource.status?.toLowerCase())
//                             : true)
//               );
    
//     const filteredActiveDataSources = filteredDataSources.filter(
//         (p: any) => p.status === "active" || p.status === "approved"
//     );
//     const filteredPendingDataSources = filteredDataSources.filter(
//         (p: any) => p.status === "pending"
//     );
//     const filteredRejectedDataSources = filteredDataSources.filter(
//         (p: any) => p.status === "rejected"
//     );

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"requests-data-sources"} />
//             <MiniBot/>
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: window.innerHeight,
//                 }}
//             >
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={2} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 justifyContent: "space-between",
//                                 alignItems: "center",
//                                 marginY: 1,
//                                 marginLeft: 2,
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
//                                 Requests
//                             </Typography>
//                             <IconButton
//                                 aria-label="Help" 
//                                 onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
//                                 sx={{ padding: 0, border: 'none', mr:2, '&:hover': { backgroundColor: 'action.hover' } }}
//                               >
//                                 <Box component="img" src="/assets/Help.svg" alt="" sx={{ width: 40,  height: 40, display: 'block' }} />
//                               </IconButton>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}>
//                             <Box>
//                                 <Typography fontFamily="Gilroy-SemiBold" fontSize="16px">
//                                     {`DATA SOURCES (${dataSources.length})`}
//                                 </Typography>
//                                 <Typography fontFamily="Gilroy-Regular" fontSize="14px">
//                                     {`View all the requests raised for Data Sources`}
//                                 </Typography>
//                             </Box>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptions}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 defaultValue={[]}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) => {
//                                     return tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
//                                     ));
//                                 }}
//                                 onChange={(e, newVal, reason) => {
//                                     setSearchQuery(newVal);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by source name, owner, status etc"
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start">
//                                                         <img src="/assets/search-normal.svg" alt="Filter Icon" style={{ width: "16px", paddingLeft: "4px" }}/>
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: { backgroundColor: "#f4f4f4", minHeight: "45px", width: "500px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
//                                         }}
//                                     />
//                                 )}
//                             />
//                         </Box>
//                     </Box>
//                     <Box m={2}>
//                         <Accordion
//                             defaultExpanded={filteredPendingDataSources.length > 0 ? true : false}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredPendingDataSources.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending Data Sources ({filteredPendingDataSources.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredPendingDataSources.length > 0 && (
//                                     <DataSourcesRequestsTable dataSources={filteredPendingDataSources} resourceStatuses={updates}/>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                         <Accordion
//                             defaultExpanded={true}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredActiveDataSources.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active Data Sources ({filteredActiveDataSources.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredActiveDataSources.length > 0 && (
//                                     <DataSourcesRequestsTable dataSources={filteredActiveDataSources} resourceStatuses={updates}/>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         <Accordion
//                             defaultExpanded={filteredRejectedDataSources.length > 0 ? true : false}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredRejectedDataSources.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected Data Sources ({filteredRejectedDataSources.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredRejectedDataSources.length > 0 && (
//                                     <DataSourcesRequestsTable dataSources={filteredRejectedDataSources} resourceStatuses={updates}/>
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default DataSourcesRequestsPage;